import React, { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "./supabase";
import { CATEGORIES } from './constants/categories';
import { ADMIN_EMAILS } from './constants/admin';
import { getCityFromZip } from './constants/zipData';
import { fmtDate, fmtTime, generateRef } from './utils/formatting';
import { dbVendorToApp, dbEventToApp } from './utils/dbTransforms';
import { getConvoId, getSessionId, CONVERSATIONS_LS_KEY, VENDOR_CALS_LS_KEY } from './utils/conversations';
import { authHeaders } from './utils/api';
import { styles } from './styles/global';
import VendorForm from './components/vendor/VendorForm';
import VendorDashboard from './components/vendor/VendorDashboard';
import HostForm from './components/host/HostForm';
import HostDashboard from './components/host/HostDashboard';
import HostGuestEventForm from './components/host/HostGuestEventForm';
import HostSuccessMatches from './components/host/HostSuccessMatches';
import MatchesPage from './components/pages/MatchesPage';
import UpcomingMarketsPage from './components/pages/UpcomingMarketsPage';
import OpportunitiesPage from './components/pages/OpportunitiesPage';
import PricingPage from './components/pages/PricingPage';
import AdminPage from './components/pages/AdminPage';
import MessagesPage from './components/pages/MessagesPage';
import TosPage from './components/pages/TosPage';
import MyCalendarPage from './components/pages/MyCalendarPage';
import EventGoerDashboard from './components/pages/EventGoerDashboard';
import AuthModal from './components/modals/AuthModal';
import ContactModal from './components/modals/ContactModal';
import FeedbackModal from './components/modals/FeedbackModal';
import EventGoerSignupModal from './components/modals/EventGoerSignupModal';
import InquiryModal from './components/modals/InquiryModal';
import EventMessageModal from './components/modals/EventMessageModal';

function AppInner() {
  const getTabFromUrl = () => {
    const path = window.location.pathname.replace(/^\//, '');
    if (path && path !== '') return path;
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'home';
  };
  const [tab, setTabRaw] = useState(getTabFromUrl);
  const setTab = (newTab) => {
    setTabRaw(newTab);
    const url = newTab === 'home' ? '/' : '/' + newTab;
    window.history.pushState({ tab: newTab }, '', url);
  };
  useEffect(() => {
    const onPop = () => {
      const t = getTabFromUrl();
      setTabRaw(t);
      window.scrollTo({ top: 0 });
    };
    window.addEventListener('popstate', onPop);
    const initTab = getTabFromUrl();
    const initUrl = initTab === 'home' ? '/' : '/' + initTab;
    window.history.replaceState({ tab: initTab }, '', initUrl);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const [authUser, setAuthUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState(null);
  const [authModalRole, setAuthModalRole] = useState(null);
  const openSignup = (role) => { setAuthModalMode('signup'); setAuthModalRole(role||null); setShowAuthModal(true); };
  const openLogin = () => { setAuthModalMode(null); setAuthModalRole(null); setShowAuthModal(true); };
  const [authEmail, setAuthEmail] = useState('');
  const [referralCode, setReferralCode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('ref') || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('sjvm_ref_code') : null) || '';
  });
  useEffect(() => { if (referralCode) try { sessionStorage.setItem('sjvm_ref_code', referralCode); } catch {} }, [referralCode]);
  const [vendorProfile, setVendorProfile] = useState(null); // active vendor profile (raw DB row)
  const [allVendorProfiles, setAllVendorProfiles] = useState([]); // all vendor profiles for this user
  const [userEvents, setUserEvents] = useState([]); // raw DB rows for logged-in host
  const [hasBeenHost, setHasBeenHost] = useState(false); // sticky flag: true once user has/had events
  const isAdmin = authUser && ADMIN_EMAILS.includes(authUser.email?.toLowerCase());

  // Listen for auth state changes — clear drafts when no session
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) clearAllDrafts();
      setAuthUser(session?.user || null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!session) clearAllDrafts();
      setAuthUser(session?.user || null);
      if (event === 'PASSWORD_RECOVERY') {
        setAuthModalMode('resetPassword');
        setShowAuthModal(true);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Home page stat counts — fetched via service-role API so they're accurate
  // for anonymous visitors (event_goers has no public read policy under RLS).
  useEffect(() => {
    fetch('/api/site-counts')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setSiteCounts(data); })
      .catch(err => console.error('site-counts fetch failed:', err));
  }, []);

  // Magic-link entry: detect ?goer_token= on first load.
  // Lets event guests manage preferences without creating a Supabase Auth account.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('goer_token') || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('sjvm_goer_token') : null);
    if (!token) return;
    fetch(`/api/get-event-goer-by-token?token=${encodeURIComponent(token)}`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error('Token lookup failed')))
      .then(({ profile }) => {
        if (profile) {
          setEventGoerProfile(profile);
          setEventGoerManageToken(token);
          // Persist token in sessionStorage so page refresh preserves access
          try { sessionStorage.setItem('sjvm_goer_token', token); } catch {}
          setTabRaw('event-goer-dashboard');
          window.history.replaceState({ tab: 'event-goer-dashboard' }, '', '/event-goer-dashboard');
        }
      })
      .catch(err => console.error('goer_token lookup failed:', err));
  }, []);

  // Post-login: check for pending signup roles and route accordingly
  useEffect(() => {
    if (!authUser) return;
    const pendingStr = localStorage.getItem('sjvm_pending_roles');
    if (!pendingStr) return;
    localStorage.removeItem('sjvm_pending_roles');
    try {
      const roles = JSON.parse(pendingStr);
      // Priority: vendor form first, then event guest preferences, then host message
      if (roles.vendor) {
        setTab('vendor');
        // If also event guest, open preferences after a short delay
        if (roles.eventGoer) setTimeout(() => setShowEventGoerSignup(true), 500);
      } else if (roles.eventGoer) {
        setShowEventGoerSignup(true);
      } else if (roles.host) {
        setTab('host');
      }
    } catch {}
  }, [authUser]);

  // Route to dashboard on login (when not a fresh signup)
  useEffect(() => {
    if (!authUser) return;
    if (localStorage.getItem('sjvm_pending_roles')) return; // fresh signup handled above
    // Only redirect to dashboard if currently on home page
    if (tab === 'home') {
      // Wait briefly for profile/events to load, then route
      const timer = setTimeout(() => {
        if (vendorProfile && userEvents.length > 0) setTab('vendor-dashboard');
        else if (vendorProfile) setTab('vendor-dashboard');
        else if (userEvents.length > 0) setTab('host-dashboard');
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [authUser, vendorProfile, userEvents]);

  // Load vendor profile and host events for logged-in user
  useEffect(() => {
    if (!authUser) { setVendorProfile(null); setAllVendorProfiles([]); setUserEvents([]); setEventGoerProfile(null); return; }
    // Load all user data sequentially to avoid race conditions
    (async () => {
      try {
        // Event guest profile
        const { data: goerData, error: goerErr } = await supabase.from('event_goers').select('*').eq('email', authUser.email).limit(1);
        if (goerErr) console.error('Failed to load event goer profile:', goerErr);
        if (goerData?.[0]) setEventGoerProfile(goerData[0]);

        // Vendor profiles — by user_id first, then by email, deduplicate
        const profiles = [];
        const { data: byId, error: byIdErr } = await supabase.from('vendors').select('*').eq('user_id', authUser.id);
        if (byIdErr) console.error('Failed to load vendors by user_id:', byIdErr);
        if (byId) profiles.push(...byId);
        const { data: byEmail, error: byEmailErr } = await supabase.from('vendors').select('*').ilike('contact_email', authUser.email);
        if (byEmailErr) console.error('Failed to load vendors by email:', byEmailErr);
        if (byEmail) {
          for (const v of byEmail) {
            if (!profiles.some(p => p.id === v.id)) {
              if (!v.user_id) supabase.from('vendors').update({ user_id: authUser.id }).eq('id', v.id).then(()=>{}).catch(err=>console.error('Link vendor user_id failed:',err));
              profiles.push(v);
            }
          }
        }
        setAllVendorProfiles(profiles);
        if (profiles.length > 0) setVendorProfile(p => p || profiles[0]);

        // Events — by user_id first, then by email, deduplicate
        const allEvts = [];
        const { data: evtsById, error: evtsByIdErr } = await supabase.from('events').select('*').eq('user_id', authUser.id).order('date', { ascending: false });
        if (evtsByIdErr) console.error('Failed to load events by user_id:', evtsByIdErr);
        if (evtsById) allEvts.push(...evtsById);
        const { data: evtsByEmail, error: evtsByEmailErr } = await supabase.from('events').select('*').ilike('contact_email', authUser.email).order('date', { ascending: false });
        if (evtsByEmailErr) console.error('Failed to load events by email:', evtsByEmailErr);
        if (evtsByEmail) {
          for (const e of evtsByEmail) {
            if (!allEvts.some(x => x.id === e.id)) {
              if (!e.user_id) supabase.from('events').update({ user_id: authUser.id }).eq('id', e.id).then(()=>{}).catch(err=>console.error('Link event user_id failed:',err));
              allEvts.push(e);
            }
          }
        }
        setUserEvents(allEvts);
        if (allEvts.length > 0) setHasBeenHost(true);
      } catch (err) {
        console.error('Failed to load user data:', err);
      }
    })();
  }, [authUser]);

  // Admin: reload events and pending vendors after login so RLS returns pending items
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      const { data: eventRows } = await supabase.from('events').select('*').gte('date', new Date().toISOString().split('T')[0]).order('date', { ascending: true });
      if (eventRows) {
        setOpps(eventRows.map(dbEventToApp).filter(e => !e.status || e.status === 'approved' || e.status === 'concierge_active'));
        setAllEvents(eventRows.map(dbEventToApp));
      }
      const { data: pendingRows } = await supabase.from('vendors').select('*').eq('status', 'pending').order('created_at', { ascending: false });
      if (pendingRows) setPendingVendors(pendingRows);
      const { data: goerRows } = await supabase.from('event_goers').select('*').eq('active', true).order('created_at', { ascending: false });
      if (goerRows) setEventGoers(goerRows);
    })();
  }, [isAdmin]);

  const clearAllDrafts = () => {
    localStorage.removeItem('sjvm_vendor_draft');
    localStorage.removeItem('sjvm_vendor_draft_subs');
    localStorage.removeItem('sjvm_host_draft');
    localStorage.removeItem('sjvm_host_draft_subs');
    localStorage.removeItem('sjvm_conversations');
    localStorage.removeItem('sjvm_vendor_calendars');
  };
  const handleLogout = async () => {
    await supabase.auth.signOut();
    clearAllDrafts();
    setAuthUser(null); setVendorProfile(null); setUserEvents([]); setHasBeenHost(false);
    setTab('home');
  };

  const [vendorSuccess, setVendorSuccess] = useState(false);
  const [vendorConfirm, setVendorConfirm] = useState(null); // { ref, email, name }
  const [hostSuccess,   setHostSuccess]   = useState(false);
  const [hostFormType,  setHostFormType]  = useState(null); // null=chooser, 'opportunity', 'guest'
  const [hostConfirm,   setHostConfirm]   = useState(null); // { ref, email, eventName }
  const [hostEvent,     setHostEvent]     = useState(null);
  const [highlightOppId, setHighlightOppId] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [opps, setOpps] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [eventGoers, setEventGoers] = useState([]);
  const [eventGoerProfile, setEventGoerProfile] = useState(null);
  const [eventGoerManageToken, setEventGoerManageToken] = useState(null); // set when accessed via ?goer_token=
  const [hostCount, setHostCount] = useState(0);
  const [siteCounts, setSiteCounts] = useState(null); // { vendors, eventGuests, events } — for home page stats
  const [acceptedBookings, setAcceptedBookings] = useState([]); // all accepted booking_requests — shared across pages
  const [showEventGoerSignup, setShowEventGoerSignup] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [inquiryModal, setInquiryModal] = useState(null); // { vendor } — for messaging without an event
  const [eventMessageModal, setEventMessageModal] = useState(null); // { vendor, eventName } — for messaging with an event
  const navTo = (t) => { setTab(t); setMobileMenuOpen(false); window.scrollTo({top:0}); };
  const [vendorSubs, setVendorSubs] = useState([]);
  const [pendingVendors, setPendingVendors] = useState([]);

  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Centralized accepted bookings loader — called on mount and every tab change
  const loadAcceptedBookings = useCallback(() => {
    supabase.from('booking_requests')
      .select('vendor_name,vendor_category,vendor_emoji,event_name,event_date,status')
      .eq('status', 'accepted')
      .then(({ data, error }) => {
        if (error) { console.error('Failed to load accepted bookings:', error); return; }
        if (data) setAcceptedBookings(data);
      });
  }, []);
  useEffect(() => { loadAcceptedBookings(); }, [tab, loadAcceptedBookings]);

  // Admin: refresh events and pending vendors when navigating to admin tab
  useEffect(() => {
    if (tab !== 'admin' || !isAdmin) return;
    (async () => {
      const { data: eventRows } = await supabase.from('events').select('*').gte('date', new Date().toISOString().split('T')[0]).order('date', { ascending: true });
      if (eventRows) {
        setOpps(eventRows.map(dbEventToApp).filter(e => !e.status || e.status === 'approved' || e.status === 'concierge_active'));
        setAllEvents(eventRows.map(dbEventToApp));
      }
      const { data: pendingRows } = await supabase.from('vendors').select('*').eq('status', 'pending').order('created_at', { ascending: false });
      if (pendingRows) setPendingVendors(pendingRows);
      const { data: goerRows } = await supabase.from('event_goers').select('*').eq('active', true).order('created_at', { ascending: false });
      if (goerRows) setEventGoers(goerRows);
    })();
  }, [tab, isAdmin]);

  // Modal back-button support
  const modalOpenRef = useRef(false);
  const anyModalOpen = showAuthModal || showContactModal || showFeedbackModal || showEventGoerSignup || mobileMenuOpen;
  useEffect(() => {
    if (anyModalOpen && !modalOpenRef.current) {
      window.history.pushState({ modal: true }, '');
      modalOpenRef.current = true;
    } else if (!anyModalOpen) {
      modalOpenRef.current = false;
    }
  }, [anyModalOpen]);

  useEffect(() => {
    const onPop = () => {
      if (modalOpenRef.current) {
        setShowAuthModal(false); setShowContactModal(false);
        setShowFeedbackModal(false); setShowEventGoerSignup(false);
        setMobileMenuOpen(false);
        modalOpenRef.current = false;
        return;
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Escape key closes any open modal
  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === 'Escape') {
        setShowAuthModal(false); setShowContactModal(false);
        setShowFeedbackModal(false); setShowEventGoerSignup(false);
        setMobileMenuOpen(false); setInquiryModal(null); setEventMessageModal(null);
      }
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, []);

  useEffect(() => {
    async function fetchData() {
      try {
      // Try loading approved vendors; if status column doesn't exist yet, load all vendors
      let vendorRows, vErr;
      const [approvedResult, eventResult] = await Promise.all([
        supabase.from('vendors').select('*').eq('status', 'approved').order('created_at', { ascending: false }),
        supabase.from('events').select('*').gte('date', new Date().toISOString().split('T')[0]).order('date', { ascending: true }),
      ]);
      vendorRows = approvedResult.data;
      vErr = approvedResult.error;
      const { data: eventRows, error: eErr } = eventResult;

      // Fallback: if status column doesn't exist, fetch all vendors
      if (vErr && vErr.code === '42703') {
        console.warn('vendors.status column not found — loading all vendors');
        const fallback = await supabase.from('vendors').select('*').order('created_at', { ascending: false });
        vendorRows = fallback.data;
        vErr = fallback.error;
      }

      if (vErr) { console.error('Failed to load vendors:', vErr); setLoadError('Could not load vendor data. Please refresh.'); }
      else if (vendorRows) setVendors(vendorRows.map(dbVendorToApp));

      // Load pending vendors for admin review (skip if status column doesn't exist)
      const { data: pendingRows, error: pErr } = await supabase.from('vendors').select('*').eq('status', 'pending').order('created_at', { ascending: false });
      if (pErr && pErr.code !== '42703') console.error('Failed to load pending vendors:', pErr);
      else if (pendingRows) setPendingVendors(pendingRows);
      if (eErr) { console.error('Failed to load events:', eErr); setLoadError('Could not load event data. Please refresh.'); }
      else if (eventRows) {
        // Public feed only shows approved events (or events without status column for backwards compat)
        setOpps(eventRows.map(dbEventToApp).filter(e => !e.status || e.status === 'approved' || e.status === 'concierge_active'));
        // Store all events including pending for admin
        setAllEvents(eventRows.map(dbEventToApp));
      }

      // Load event guests for admin
      supabase.from('event_goers').select('*').eq('active', true).order('created_at', { ascending: false })
        .then(({ data }) => { if (data) setEventGoers(data); });

      // Count distinct hosts (events with unique user_id or contact_email)
      supabase.from('events').select('user_id,contact_email')
        .then(({ data }) => {
          if (data) {
            const unique = new Set(data.map(e => e.user_id || e.contact_email).filter(Boolean));
            setHostCount(unique.size);
          }
        });

      // Load booking requests for this browser session from Supabase
      const sid = getSessionId();
      const { data: brRows, error: brErr } = await supabase
        .from('booking_requests').select('*').eq('session_id', sid).order('created_at', { ascending: false });
      if (brErr) console.error('Failed to load booking requests:', brErr);
      else if (brRows && brRows.length > 0) {
        setBookingRequests(brRows.map(r => ({
          id: r.id, vendorId: r.vendor_id, vendorName: r.vendor_name,
          vendorEmoji: r.vendor_emoji, vendorCategory: r.vendor_category,
          hostName: r.host_name, hostEmail: r.host_email,
          eventName: r.event_name, eventType: r.event_type,
          eventZip: r.event_zip, eventDate: r.event_date,
          startTime: r.start_time, endTime: r.end_time, address: r.address,
          attendance: r.attendance, vendorCount: r.vendor_count,
          budget: r.budget, notes: r.notes,
          isRecurring: r.is_recurring, recurrenceFrequency: r.recurrence_frequency,
          recurrenceDay: r.recurrence_day, recurrenceEndType: r.recurrence_end_type,
          recurrenceEndDate: r.recurrence_end_date, recurrenceCount: r.recurrence_count,
          recurrenceNotes: r.recurrence_notes,
          categoriesNeeded: r.categories_needed || [], subcategoriesNeeded: r.subcategories_needed || [],
          status: r.status, sentAt: r.sent_at, respondedAt: r.responded_at,
          vendorMessage: r.vendor_message || '',
        })));
      }
      } catch (err) {
        console.error('fetchData error:', err);
        setLoadError('Something went wrong loading data. Please refresh.');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // Poll for booking request status updates (vendor responses via email link)
  useEffect(() => {
    const interval = setInterval(async () => {
      const sid = getSessionId();
      const { data: rows } = await supabase
        .from('booking_requests').select('id,status,vendor_message,responded_at')
        .eq('session_id', sid);
      if (rows) {
        setBookingRequests(prev => prev.map(r => {
          const updated = rows.find(u => u.id === r.id);
          if (updated && updated.status !== r.status) {
            return { ...r, status: updated.status, vendorMessage: updated.vendor_message || '', respondedAt: updated.responded_at };
          }
          return r;
        }));
      }
    }, 30000); // every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Conversations: persist to localStorage so they survive page refresh
  const [conversations, setConversations] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Load conversations from Supabase
  const loadMessages = useCallback(async () => {
    if (!authUser) { setConversations([]); setUnreadCount(0); return; }
    try {
    const uid = authUser.id;
    const { data: msgs, error } = await supabase.from('messages').select('*')
      .or(`sender_id.eq.${uid},recipient_id.eq.${uid}`)
      .order('created_at', { ascending: true });
    if (error || !msgs) return;
    // Group by conversation_id
    // Collect unique vendor and host IDs to look up names
    const vendorIds = new Set();
    const hostIds = new Set();
    msgs.forEach(m => {
      if (m.sender_type === 'vendor') vendorIds.add(m.sender_id);
      if (m.recipient_type === 'vendor') vendorIds.add(m.recipient_id);
      if (m.sender_type === 'host') hostIds.add(m.sender_id);
      if (m.recipient_type === 'host') hostIds.add(m.recipient_id);
    });
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    // Look up vendor names
    const validVendorIds = [...vendorIds].filter(id => uuidRegex.test(id));
    const vendorNames = {};
    if (validVendorIds.length > 0) {
      const { data: vendorRows } = await supabase.from('vendors').select('user_id,name,contact_name').in('user_id', validVendorIds);
      if (vendorRows) vendorRows.forEach(v => { if (v.user_id) vendorNames[v.user_id] = v.name || v.contact_name || 'Vendor'; });
    }
    // Look up host names from events table
    const validHostIds = [...hostIds].filter(id => uuidRegex.test(id) && id !== uid);
    const hostNames = {};
    if (validHostIds.length > 0) {
      const { data: hostRows } = await supabase.from('events').select('user_id,contact_name,org_name').in('user_id', validHostIds);
      if (hostRows) hostRows.forEach(e => { if (e.user_id && !hostNames[e.user_id]) hostNames[e.user_id] = e.org_name || e.contact_name || 'Host'; });
    }
    const convMap = {};
    msgs.forEach(m => {
      if (!convMap[m.conversation_id]) convMap[m.conversation_id] = { id: m.conversation_id, messages: [], vendorId: null, vendorName: '', hostName: '', hostId: null, eventName: m.event_name || '', status: 'active' };
      const conv = convMap[m.conversation_id];
      if (m.sender_type === 'vendor') { conv.vendorId = m.sender_id; conv.vendorName = conv.vendorName || vendorNames[m.sender_id] || 'Vendor'; }
      if (m.sender_type === 'host') { conv.hostId = conv.hostId || m.sender_id; conv.hostName = conv.hostName || hostNames[m.sender_id] || 'Host'; }
      if (m.recipient_type === 'vendor') { conv.vendorId = conv.vendorId || m.recipient_id; conv.vendorName = conv.vendorName || vendorNames[m.recipient_id] || 'Vendor'; }
      if (m.recipient_type === 'host') { conv.hostId = conv.hostId || m.recipient_id; conv.hostName = conv.hostName || hostNames[m.recipient_id] || 'Host'; }
      if (m.event_name) conv.eventName = m.event_name;
      const senderLabel = m.sender_type === 'system' ? 'System' : m.sender_type === 'vendor' ? (vendorNames[m.sender_id] || 'Vendor') : (m.sender_id === uid ? 'You' : (hostNames[m.sender_id] || 'Host'));
      conv.messages.push({ id: m.id, from: m.sender_id === uid ? 'host' : (m.sender_type === 'system' ? 'system' : 'vendor'), text: m.message_text, ts: m.created_at, senderName: senderLabel, attachments: m.attachments || undefined });
    });
    // Filter out broken conversations (must contain current user's ID and have messages)
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}/i;
    const validConvos = Object.values(convMap).filter(c => {
      // Must have the current user's ID in the conversation_id
      if (!c.id.includes(uid)) return false;
      // First two segments of conversation_id must look like UUIDs
      // Format: uuid1_uuid2 or uuid1_uuid2_evt_eventId
      const parts = c.id.split('_');
      if (parts.length < 2 || !uuidPattern.test(parts[0]) || !uuidPattern.test(parts[1])) return false;
      // Must have at least one message
      if (c.messages.length === 0) return false;
      return true;
    });
    // Merge: keep locally-created conversations (pending_first_message) that aren't in DB yet
    setConversations(prev => {
      const dbIds = new Set(validConvos.map(c => c.id));
      const localPending = prev.filter(c => c.status === 'pending_first_message' && !dbIds.has(c.id));
      return [...localPending, ...validConvos];
    });
    setUnreadCount(msgs.filter(m => m.recipient_id === uid && !m.is_read).length);
    } catch (e) { console.error('loadMessages error:', e); }
  }, [authUser]);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  // Realtime subscription for instant message delivery
  useEffect(() => {
    if (!authUser) return;
    let debounceTimer = null;
    const debouncedLoad = () => { clearTimeout(debounceTimer); debounceTimer = setTimeout(loadMessages, 300); };
    const channel = supabase.channel(`messages-${authUser.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages',
        filter: `recipient_id=eq.${authUser.id}` },
        () => debouncedLoad()
      )
      .subscribe();
    // Fallback polling every 60 seconds in case realtime disconnects
    const interval = setInterval(loadMessages, 60000);
    return () => { clearTimeout(debounceTimer); supabase.removeChannel(channel); clearInterval(interval); };
  }, [authUser, loadMessages]);

  // Migrate localStorage messages to Supabase on first load
  useEffect(() => {
    if (!authUser) return;
    const lsKey = CONVERSATIONS_LS_KEY;
    try {
      const old = JSON.parse(localStorage.getItem(lsKey) || '[]');
      if (old.length === 0) return;
      const uid = authUser.id;
      const inserts = [];
      old.forEach(conv => {
        const convoId = conv.vendorId ? `${uid}_${conv.vendorId}` : conv.id?.toString() || `legacy_${Date.now()}`;
        (conv.messages || []).forEach(msg => {
          if (msg.from === 'system') return;
          inserts.push({
            conversation_id: convoId,
            sender_id: msg.from === 'host' ? uid : (conv.vendorId || 'unknown'),
            sender_type: msg.from === 'host' ? 'host' : 'vendor',
            recipient_id: msg.from === 'host' ? (conv.vendorId || 'unknown') : uid,
            recipient_type: msg.from === 'host' ? 'vendor' : 'host',
            event_name: conv.eventName || '',
            message_text: msg.text || '(file attachment)',
            attachments: msg.attachments || null,
            is_read: true,
            created_at: msg.ts || new Date().toISOString(),
          });
        });
      });
      if (inserts.length > 0) {
        supabase.from('messages').insert(inserts).then(() => {
          localStorage.removeItem(lsKey);
          loadMessages();
        }).catch(err => console.error('Offline message sync failed:', err));
      }
    } catch {}
  }, [authUser]);

  const [activeConvoId, setActiveConvoId] = useState(null);
  const [bookingRequests, setBookingRequests] = useState([]);
  const [calendarVendorId, setCalendarVendorId] = useState(() => localStorage.getItem('sjvm_calendar_vendor_id') || null);

  // Vendor calendars: persist to localStorage
  const [vendorCalendars, setVendorCalendars] = useState(() => {
    try { return JSON.parse(localStorage.getItem(VENDOR_CALS_LS_KEY) || '{}'); } catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem(VENDOR_CALS_LS_KEY, JSON.stringify(vendorCalendars)); } catch {}
  }, [vendorCalendars]);

  const sendBookingRequest = async (vendor, eventDetails) => {
    // Check if vendor already has a booking for this specific event date
    let dupQuery = supabase.from('booking_requests').select('id,status').eq('vendor_id', vendor.id).eq('event_name', eventDetails.eventName);
    if (eventDetails.date) dupQuery = dupQuery.eq('event_date', eventDetails.date);
    const { data: existingBooking } = await dupQuery.limit(1);
    if (existingBooking?.[0]) {
      alert(`${vendor.name} already has a ${existingBooking[0].status} application for ${eventDetails.eventName}${eventDetails.date ? ' on ' + eventDetails.date : ''}.`);
      return;
    }
    const responseToken = crypto.randomUUID();
    const req = {
      id: Date.now(),
      vendorId: vendor.id,
      vendorName: vendor.name,
      vendorEmoji: vendor.emoji,
      vendorCategory: vendor.category,
      hostName: eventDetails.contactName || 'Host',
      hostEmail: eventDetails.email || '',
      eventId: eventDetails.eventId || null,
      eventName: eventDetails.eventName || '',
      eventType: eventDetails.eventType || '',
      eventZip: eventDetails.eventZip || '',
      eventDate: eventDetails.date || '',
      startTime: eventDetails.startTime || '',
      endTime: eventDetails.endTime || '',
      address: eventDetails.address || '',
      attendance: eventDetails.expectedAttendance || '',
      vendorCount: eventDetails.vendorCount || '',
      budget: eventDetails.budget || '',
      notes: eventDetails.notes || '',
      isRecurring: eventDetails.isRecurring || false,
      recurrenceFrequency: eventDetails.recurrenceFrequency || '',
      recurrenceDay: eventDetails.recurrenceDay || '',
      recurrenceEndType: eventDetails.recurrenceEndType || '',
      recurrenceEndDate: eventDetails.recurrenceEndDate || '',
      recurrenceCount: eventDetails.recurrenceCount || '',
      recurrenceNotes: eventDetails.recurrenceNotes || '',
      categoriesNeeded: eventDetails.vendorCategories || [],
      subcategoriesNeeded: eventDetails.vendorSubcategories || [],
      status: 'pending',
      sentAt: new Date().toISOString(),
      respondedAt: null,
      vendorMessage: '',
      responseToken,
    };
    setBookingRequests(r => [req, ...r]);
    // Persist to Supabase so the request survives page refreshes
    const brPayload = {
      id: req.id, session_id: getSessionId(),
      vendor_id: req.vendorId, vendor_name: req.vendorName,
      vendor_emoji: req.vendorEmoji, vendor_category: req.vendorCategory,
      host_name: req.hostName, host_email: req.hostEmail,
      event_id: req.eventId || null,
      event_name: req.eventName, event_type: req.eventType,
      event_zip: req.eventZip, event_date: req.eventDate,
      start_time: req.startTime, end_time: req.endTime, address: req.address,
      attendance: req.attendance, vendor_count: req.vendorCount,
      budget: req.budget, notes: req.notes,
      is_recurring: req.isRecurring, recurrence_frequency: req.recurrenceFrequency,
      recurrence_day: req.recurrenceDay, recurrence_end_type: req.recurrenceEndType,
      recurrence_end_date: req.recurrenceEndDate || null,
      recurrence_count: req.recurrenceCount, recurrence_notes: req.recurrenceNotes,
      categories_needed: req.categoriesNeeded, subcategories_needed: req.subcategoriesNeeded,
      status: req.status, sent_at: req.sentAt,
      response_token: responseToken,
    };
    let { error: brErr } = await supabase.from('booking_requests').insert(brPayload);
    if (brErr) {
      console.error('Booking insert failed, retrying without optional columns:', brErr.message);
      const { response_token:_rt, recurrence_frequency:_rf, recurrence_day:_rd, recurrence_end_type:_ret, recurrence_end_date:_red, recurrence_count:_rc, recurrence_notes:_rn, categories_needed:_cn, subcategories_needed:_sn, is_recurring:_ir, event_id:_ei, ...safePayload } = brPayload;
      const { error: brErr2 } = await supabase.from('booking_requests').insert(safePayload);
      if (brErr2) console.error('Booking insert retry also failed:', brErr2.message);
      else brErr = null;
    }

    // Look up vendor email and send notification
    if (!brErr) {
      const { data: vendorRow } = await supabase.from('vendors').select('contact_email').eq('id', vendor.id).single();
      const vendorEmail = vendorRow?.contact_email;
      if (vendorEmail) {
        try {
          await fetch('/api/send-booking-email', {
            method: 'POST',
            headers: await authHeaders(),
            body: JSON.stringify({
              vendorEmail, vendorName: vendor.name,
              hostName: req.hostName, hostEmail: req.hostEmail,
              eventName: req.eventName, eventType: req.eventType,
              eventDate: req.eventDate, startTime: req.startTime,
              endTime: req.endTime, eventZip: req.eventZip,
              address: req.address, attendance: req.attendance,
              vendorCount: req.vendorCount, budget: req.budget,
              notes: req.notes, isRecurring: req.isRecurring,
              recurrenceFrequency: req.recurrenceFrequency,
              recurrenceDay: req.recurrenceDay,
              responseToken,
            }),
          });
        } catch (emailErr) {
          console.error('Failed to send booking email:', emailErr);
        }
      }
    }

  };

  const openMessage = async (vendor, inquiryType, inquiryMessage) => {
    const uid = authUser?.id || 'anon';
    // Resolve vendor's auth user_id (different from vendors table row id)
    let vendorAuthId = vendor.userId || vendor.user_id || null;
    if (!vendorAuthId) {
      const { data } = await supabase.from('vendors').select('user_id').eq('id', vendor.id).limit(1);
      vendorAuthId = data?.[0]?.user_id;
    }
    if (!vendorAuthId) { alert('Unable to message this vendor — their account may not be linked. Please try again later.'); return; }
    const hasEvent = !!hostEvent?.eventName;
    const eventId = hasEvent ? (hostEvent.eventId || null) : null;
    const convoId = getConvoId(uid, vendorAuthId, eventId);
    const existing = conversations.find(c => c.id === convoId);
    if (existing) { setActiveConvoId(convoId); setTab("messages"); return; }
    const eventLabel = hasEvent ? (hostEvent.eventName + (hostEvent.date ? ' — ' + fmtDate(hostEvent.date) : '')) : null;
    const contextLabel = eventLabel || inquiryType || 'General Inquiry';
    const eventDetails = hasEvent ? [
      hostEvent.eventName,
      hostEvent.date ? fmtDate(hostEvent.date) : null,
      hostEvent.startTime ? fmtTime(hostEvent.startTime) + (hostEvent.endTime ? ' – ' + fmtTime(hostEvent.endTime) : '') : null,
      hostEvent.eventZip ? (getCityFromZip(hostEvent.eventZip) || 'Zip ' + hostEvent.eventZip) : null,
      hostEvent.notes ? 'Notes: ' + hostEvent.notes : null,
    ].filter(Boolean).join(' · ') : null;
    const sysText = hasEvent
      ? `Conversation about ${eventLabel}.\n${eventDetails}\nContact info is shared only after a booking is confirmed.`
      : `${authUser?.email || 'A host'} started a conversation with ${vendor.name}. Inquiry: ${contextLabel}.${inquiryMessage ? ' "'+inquiryMessage+'"' : ''}\nContact info is shared only after a booking is confirmed.`;
    const evtName = eventLabel || contextLabel;
    // Build conversation messages
    const ts = new Date().toISOString();
    const msgs = [{ id: Date.now(), from: 'system', text: sysText, ts }];
    // Determine host display name from events
    const hostDisplayName = (() => {
      if (userEvents && userEvents.length > 0) {
        const e = userEvents[0];
        return e.org_name || e.contact_name || null;
      }
      return authUser?.user_metadata?.full_name || null;
    })() || authUser?.email || 'Host';
    // If there's an actual user message, add it
    if (inquiryMessage && inquiryMessage.trim()) {
      msgs.push({ id: Date.now()+1, from: 'host', senderName: hostDisplayName, text: inquiryMessage.trim(), ts });
    }
    const newConvo = {
      id: convoId, vendorId: vendorAuthId, vendorName: vendor.name,
      vendorEmoji: vendor.emoji || '', vendorCategory: vendor.category || '',
      hostName: hostDisplayName, eventName: evtName, status: 'active',
      messages: msgs,
    };
    setConversations(c => [newConvo, ...c.filter(x=>x.id!==convoId)]);
    setActiveConvoId(convoId);
    setTab("messages");
    // Save to Supabase in background — system messages for both parties
    supabase.from('messages').insert({ conversation_id: convoId, sender_id: 'system', sender_type: 'system', recipient_id: uid, recipient_type: 'host', event_name: evtName, message_text: sysText, is_read: true }).then(({error})=>{if(error)console.error('openMessage sys1:',error.message);});
    supabase.from('messages').insert({ conversation_id: convoId, sender_id: 'system', sender_type: 'system', recipient_id: vendorAuthId, recipient_type: 'vendor', event_name: evtName, message_text: sysText, is_read: false }).then(({error})=>{if(error)console.error('openMessage sys2:',error.message);});
    // Save user message if provided
    if (inquiryMessage && inquiryMessage.trim()) {
      supabase.from('messages').insert({ conversation_id: convoId, sender_id: uid, sender_type: 'host', recipient_id: vendorAuthId, recipient_type: 'vendor', event_name: evtName, message_text: inquiryMessage.trim(), is_read: false }).then(({error})=>{if(error)console.error('openMessage userMsg:',error.message);});
    }
    // Send vendor email notification with event details
    const { data: vendorRow } = await supabase.from('vendors').select('contact_email,contact_name').eq('id', vendor.id).single();
    if (vendorRow?.contact_email) {
      const emailPreview = inquiryMessage ? inquiryMessage.trim() : (eventDetails ? 'Event: ' + eventDetails : '');
      authHeaders().then(h => fetch('/api/send-message-notification', { method:'POST', headers: h,
        body: JSON.stringify({ recipientEmail: vendorRow.contact_email, recipientName: vendorRow.contact_name || vendor.name, senderName: hostDisplayName, senderType: 'host', recipientType: 'vendor', eventName: evtName, messagePreview: emailPreview }),
      })).catch(e=>console.error('API call failed:',e));
    }
  };

  // Vendor messages an event host from Opportunities page
  const messageEventHost = async (opp) => {
    if (!authUser) return;
    const uid = authUser.id;
    // Look up host's auth user_id from the event
    let hostUserId = null;
    if (opp.userId) { hostUserId = opp.userId; }
    else {
      const { data } = await supabase.from('events').select('user_id').eq('id', opp.id).limit(1);
      hostUserId = data?.[0]?.user_id;
    }
    if (!hostUserId) {
      const { data } = await supabase.from('events').select('user_id').eq('event_name', opp.eventName).limit(1);
      hostUserId = data?.[0]?.user_id;
    }
    if (!hostUserId) { alert('Unable to message this host. Please try again later.'); return; }
    const convoId = getConvoId(uid, hostUserId, opp.id || null);
    const existing = conversations.find(c => c.id === convoId);
    if (existing) { setActiveConvoId(convoId); setTab('messages'); return; }
    const eventLabel = (opp.eventName || '') + (opp.date ? ' — ' + fmtDate(opp.date) : '');
    // Create conversation locally but don't insert system messages or email the host yet.
    // Those happen when the vendor actually sends their first message (handled in the messages UI).
    const newConvo = {
      id: convoId, vendorId: uid, vendorName: vendorProfile?.name || '',
      vendorEmoji: vendorProfile?.emoji || '', vendorCategory: vendorProfile?.category || '',
      hostName: opp.contactName || 'Host', hostId: hostUserId, hostEmail: opp.contactEmail || '',
      eventName: eventLabel, status: 'pending_first_message',
      messages: [],
    };
    setConversations(c => [newConvo, ...c]);
    setActiveConvoId(convoId);
    setTab('messages');
  };

  const handleVendorSubmit = async (form, files = {}) => {
    if (!form.businessName || !form.email) {
      alert("Please fill in Business Name and Email.");
      return;
    }
    const hasMarketCats = form.categories && form.categories.length > 0;
    const hasServiceCats = form.serviceCategories && form.serviceCategories.length > 0;
    const isMarket = form.vendorType?.market;
    const isService = form.vendorType?.service;
    if (!isMarket && !isService) {
      alert("Please select whether you are a Market Vendor, Event Service Provider, or both.");
      return;
    }
    if (isMarket && !hasMarketCats) {
      alert("Please select at least one product category for your Market Vendor profile.");
      return;
    }
    if (isService && !hasServiceCats) {
      alert("Please select at least one service category for your Service Provider profile.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(form.email)) {
      alert("Please enter a valid email address.");
      return;
    }
    if (form.phone && form.phone.replace(/\D/g,'').length < 10) {
      alert("Please enter a valid 10-digit phone number, or leave it blank.");
      return;
    }
    if (!form.homeZip || !/^\d{5}$/.test(form.homeZip)) {
      alert("Please enter a valid 5-digit home zip code.");
      return;
    }
    // Create auth account if not already logged in
    let userId = authUser?.id || null;
    if (!authUser) {
      if (!form.password || form.password.length < 6) {
        alert("Please create a password (minimum 6 characters) for your vendor account.");
        return;
      }
      const { data: signUpData, error: signUpErr } = await supabase.auth.signUp({ email: form.email, password: form.password });
      if (signUpErr) {
        if (signUpErr.message?.includes('already registered')) {
          alert("An account with this email already exists. Please log in first, then submit your vendor profile.");
          setShowAuthModal(true); setAuthEmail(form.email);
          return;
        }
        alert(`Account creation failed: ${signUpErr.message}`);
        return;
      }
      userId = signUpData.user?.id || null;
    }
    // Check for duplicate vendor email
    const { data: existing } = await supabase.from('vendors').select('id').ilike('contact_email', form.email).limit(1);
    if (existing && existing.length > 0) {
      if (!window.confirm('A vendor profile with this email already exists. Submit another profile anyway?')) return;
    }
    const metadataPayload = {
      facebook: form.facebook || null,
      tiktok: form.tiktok || null,
      youtube: form.youtube || null,
      otherSocial: form.otherSocial || null,
      responseTime: form.responseTime,
      bookingLeadTime: form.bookingLeadTime,
      eventFrequency: form.eventFrequency,
      setupTime: form.setupTime,
      tableSize: form.tableSize,
      needsElectric: form.needsElectric,
      yearsActive: form.yearsActive || null,
      allCategories: form.categories,
      vendorType: form.vendorType || {market:false,service:false},
      serviceCategories: form.serviceCategories || [],
      serviceSubcategories: form.serviceSubcategories || [],
      isServiceProvider: form.vendorType?.service || form.isServiceProvider || false,
      serviceType: form.serviceType || form.serviceSubcategories?.[0] || form.serviceCategories?.[0] || null,
      serviceRateMin: form.serviceRateMin || null,
      serviceRateMax: form.serviceRateMax || null,
      serviceRateType: form.serviceRateType || 'fixed',
      minBookingDuration: form.minBookingDuration || null,
      serviceDescription: form.serviceDescription || null,
      priceMax: form.priceMax || null,
      emailFrequency: form.emailFrequency || null,
    };
    const genReferralCode = () => 'SJVM-' + Math.random().toString(36).slice(2, 8).toUpperCase();
    const vendorPayload = {
      name:                form.businessName,
      contact_name:        form.ownerName     || null,
      category:            form.categories?.[0] || form.serviceCategories?.[0] || CATEGORIES[0],
      subcategories:       form.subcategories || [],
      home_zip:            form.homeZip,
      radius:              form.radius,
      tags:                form.eventTypes    || [],
      description:         form.description,
      insurance:           form.insurance,
      has_min_purchase:    form.hasMinPurchase,
      min_purchase_amt:    form.minPurchaseAmt  || 0,
      charges_private_fee: form.chargesPrivateFee,
      private_event_fee:   form.privateEventFee || 0,
      contact_email:       form.email,
      contact_phone:       form.phone       || null,
      website:             form.website     || null,
      instagram:           form.instagram   || null,
      metadata:            metadataPayload,
      status:              'pending',
      referral_code:       genReferralCode(),
      referred_by:         form.referralCode || referralCode || null,
      ...(userId ? { user_id: userId } : {}),
    };
    let { data: newVendor, error } = await supabase.from('vendors').insert(vendorPayload).select('id').single();
    // Retry without status if the column doesn't exist yet
    if (error && error.code === '42703') {
      const { status: _s, ...payloadWithoutStatus } = vendorPayload;
      ({ data: newVendor, error } = await supabase.from('vendors').insert(payloadWithoutStatus).select('id').single());
    }
    if (error) {
      console.error('Vendor submit error:', error);
      alert(`Submission failed: ${error.message}\n\nIf this keeps happening, please contact support@southjerseyvendormarket.com`);
      return;
    }

    // Create referral tracking row if referred by another vendor
    if (newVendor?.id && vendorPayload.referred_by) {
      supabase.from('vendors').select('id').eq('referral_code', vendorPayload.referred_by).limit(1)
        .then(({ data: referrerRows }) => {
          if (referrerRows?.[0]) {
            supabase.from('referrals').insert({
              referrer_vendor_id: referrerRows[0].id,
              referred_vendor_id: newVendor.id,
              referral_code: vendorPayload.referred_by,
              status: 'pending',
            }).then(({ error: refErr }) => { if (refErr) console.error('Failed to create referral:', refErr); });
          }
        }).catch(err => console.error('Referral lookup failed:', err));
      // Clear stored referral code
      try { sessionStorage.removeItem('sjvm_ref_code'); } catch {}
    }

    // Upload files to Supabase Storage
    if (newVendor?.id) {
      const bucket = 'vendor-files';
      const vid = newVendor.id;
      const safeName = (name) => name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 100);
      const getContentType = (file) => {
        if (file.type && file.type !== 'application/octet-stream') return file.type;
        const ext = file.name?.split('.').pop()?.toLowerCase();
        const types = {jpg:'image/jpeg',jpeg:'image/jpeg',png:'image/png',gif:'image/gif',webp:'image/webp',heic:'image/heic',heif:'image/heif',pdf:'application/pdf'};
        return types[ext] || 'application/octet-stream';
      };
      const uploadFile = async (file, path) => {
        if (file.size > 10 * 1024 * 1024) { console.error('File too large:', file.name, (file.size/1024/1024).toFixed(1)+'MB'); return null; }
        const { error: upErr } = await supabase.storage.from(bucket).upload(path, file, { upsert: true, contentType: getContentType(file) });
        if (upErr) { console.error('File upload error:', upErr); return null; }
        return supabase.storage.from(bucket).getPublicUrl(path).data.publicUrl;
      };
      const { photoFiles = [], coiFile = null, lookbookFile = null } = files;
      const fileUrls = {};
      if (photoFiles.length > 0) {
        const urls = await Promise.all(photoFiles.map((f, i) => uploadFile(f, `${vid}/photos/${i}-${safeName(f.name)}`)));
        fileUrls.photoUrls = urls.filter(Boolean);
      }
      if (coiFile) {
        const url = await uploadFile(coiFile, `${vid}/coi/${safeName(coiFile.name)}`);
        if (url) fileUrls.coiUrl = url;
      }
      if (lookbookFile) {
        const url = await uploadFile(lookbookFile, `${vid}/lookbook/${safeName(lookbookFile.name)}`);
        if (url) fileUrls.lookbookUrl = url;
      }
      if (Object.keys(fileUrls).length > 0) {
        const { error: metaErr } = await supabase.from('vendors').update({ metadata: { ...metadataPayload, ...fileUrls } }).eq('id', vid);
        if (metaErr) console.error('Failed to save file URLs:', metaErr);
      }
      setCalendarVendorId(vid);
      localStorage.setItem('sjvm_calendar_vendor_id', vid);
    }

    setVendorSubs(v => [form, ...v]);
    if (newVendor?.id) {
      // Re-fetch the vendor from DB to get the complete record with file URLs
      const { data: fullVendor } = await supabase.from('vendors').select('*').eq('id', newVendor.id).single();
      if (fullVendor) {
        setPendingVendors(p => [fullVendor, ...p]);
      } else {
        setPendingVendors(p => [{ id: newVendor.id, name: form.businessName, contact_name: form.ownerName, category: form.categories?.[0] || form.serviceCategories?.[0] || CATEGORIES[0], home_zip: form.homeZip, radius: form.radius, contact_email: form.email, contact_phone: form.phone, status: 'pending', created_at: new Date().toISOString(), metadata: { ...metadataPayload }, subcategories: form.subcategories || [] }, ...p]);
      }
    }
    // Send notification emails
    try {
      const res = await fetch('/api/send-vendor-notification', {
        method: 'POST', headers: await authHeaders(),
        body: JSON.stringify({
          businessName: form.businessName,
          contactName: form.ownerName,
          vendorEmail: form.email,
          phone: form.phone,
          category: form.categories?.[0] || form.serviceCategories?.[0] || CATEGORIES[0],
          vendorType: form.vendorType?.market && form.vendorType?.service ? 'both' : form.vendorType?.service ? 'service' : 'market',
        }),
      });
      if (!res.ok) console.warn('Vendor signup notification email returned non-200:', res.status);
    } catch (e) { console.warn('Vendor signup notification email failed:', e); }

    setVendorConfirm({ ref: generateRef(), email: form.email, name: form.businessName });
    setVendorSuccess(true);
    window.scrollTo({top:0, behavior:"smooth"});
  };

  const handleHostSubmit = async (form, files = {}, opts = {}) => {
    const adminMode = !!opts.adminMode;
    if (!adminMode && (!form.contactName || !form.email)) {
      alert('Please fill in Contact Name and Email.');
      return;
    }
    if (!(form.eventTypes||[]).length && !form.eventType) {
      alert('Please select at least one Event Type.');
      return;
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(form.email)) {
      alert('Please enter a valid email address.');
      return;
    }
    if (form.phone && form.phone.replace(/\D/g,'').length < 10) {
      alert('Please enter a valid 10-digit phone number, or leave it blank.');
      return;
    }
    if (form.eventLink && !/^https?:\/\/.+/.test(form.eventLink)) {
      alert('Please provide a valid URL (starting with http:// or https://) or leave the field blank.');
      return;
    }
    if (!form.date) {
      alert('Please select an event date.');
      return;
    }
    if (form.date < new Date().toISOString().split('T')[0]) {
      alert('Event date cannot be in the past.');
      return;
    }
    if (!form.eventZip || !/^\d{5}$/.test(form.eventZip)) {
      alert('Please enter a valid 5-digit event zip code.');
      return;
    }
    if (form.isRecurring) {
      if (!form.recurrenceFrequency) {
        alert('Please select how often the event recurs.');
        return;
      }
      if (form.recurrenceEndType === 'date' && !form.recurrenceEndDate) {
        alert('Please select an end date for your recurring event series.');
        return;
      }
      if (form.recurrenceEndType === 'count' && (!form.recurrenceCount || Number(form.recurrenceCount) < 1)) {
        alert('Please enter a valid number of occurrences (minimum 1).');
        return;
      }
      if (form.recurrenceFrequency === 'monthly' && form.recurrenceMonthType === 'dayofweek' && !form.recurrenceMonthDay) {
        alert('Please select the day of the week for your monthly recurring event.');
        return;
      }
    }
    // Create auth account if not already logged in (skipped in adminMode — admin is posting on behalf of someone)
    let hostUserId = authUser?.id || null;
    if (!authUser && !adminMode) {
      if (!form.password || form.password.length < 6) {
        alert("Please create a password (minimum 6 characters) for your host account.");
        return;
      }
      const { data: signUpData, error: signUpErr } = await supabase.auth.signUp({ email: form.email, password: form.password });
      if (signUpErr) {
        if (signUpErr.message?.includes('already registered')) {
          alert("An account with this email already exists. Please log in first, then post your event.");
          setShowAuthModal(true); setAuthEmail(form.email);
          return;
        }
        alert(`Account creation failed: ${signUpErr.message}`);
        return;
      }
      hostUserId = signUpData.user?.id || null;
    }
    const eventPayload = {
      event_name: form.eventName || form.eventType,
      org_name: form.orgName || null,
      event_type: Array.isArray(form.eventTypes) ? form.eventTypes[0] : (form.eventType || 'Other'),
      event_types: Array.isArray(form.eventTypes) ? form.eventTypes : [form.eventType].filter(Boolean),
      zip: form.eventZip,
      date: form.date,
      start_time: form.startTime || null,
      end_time: form.endTime || null,
      booth_fee: form.budget || null,
      spots: form.vendorCount || null,
      categories_needed: form.vendorCategories || [],
      contact_name: form.contactName,
      contact_email: form.email,
      contact_phone: form.phone || null,
      notes: form.vendorNotes || form.eventGoerNotes || form.notes || null,
      vendor_notes: form.vendorNotes || form.notes || null,
      event_goer_notes: form.eventGoerNotes || null,
      share_with_event_goers: form.shareWithEventGoers !== false,
      deadline: form.applyByDate || null,
      source: form.isRecurring ? 'Recurring Series' : (adminMode ? (form.source || 'Direct Entry') : 'Host Submitted'),
      status: adminMode ? 'approved' : 'pending_review',
      event_link: form.eventLink || null,
      vendor_link: form.vendorLink || null,
      is_ticketed: form.isTicketedEvent || false,
      ticket_price: form.isTicketedEvent ? (form.ticketPrice || null) : null,
      services_needed: form.servicesNeeded.length > 0 ? JSON.stringify(form.servicesNeeded) : null,
      allow_duplicate_categories: form.allowDuplicateCategories,
      allow_duplicate_subcategories: form.allowDuplicateSubcategories,
      vendor_discovery: form.vendorDiscovery || 'both',
      address: form.address || null,
      expected_attendance: form.expectedAttendance || null,
      indoor_outdoor: form.indoorOutdoor || null,
      electric_available: form.electricAvailable || false,
      table_provided: form.tableProvided || false,
      table_size: form.tableSize || null,
      vendor_subcategories: form.vendorSubcategories || [],
      ...(hostUserId ? { user_id: hostUserId } : {}),
    };
    let { data: newEvent, error: eventErr } = await supabase.from('events').insert(eventPayload).select().single();
    // Retry without newer columns if they don't exist yet
    if (eventErr && (eventErr.code === '42703' || eventErr.code === 'PGRST204' || eventErr.message?.includes('column'))) {
      const { vendor_discovery: _vd, event_link: _el, vendor_notes: _vn, event_goer_notes: _egn, share_with_event_goers: _sweg, allow_duplicate_categories: _adc, allow_duplicate_subcategories: _ads, address: _addr, expected_attendance: _ea, indoor_outdoor: _io, electric_available: _elec, table_provided: _tp, table_size: _ts, vendor_subcategories: _vsub, event_types: _ets, ...fallback } = eventPayload;
      ({ data: newEvent, error: eventErr } = await supabase.from('events').insert(fallback).select().single());
    }
    if (eventErr) {
      console.error('Event submit error:', eventErr);
      alert(`Failed to submit event: ${eventErr.message}\n\nPlease try again or contact support@southjerseyvendormarket.com`);
      return;
    }
    // Send concierge email with payment link
    // Upload event photos
    if (newEvent && files.eventPhotos && files.eventPhotos.length > 0) {
      const bucket = 'vendor-files';
      const eid = newEvent.id;
      const safeName = (n) => n.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 100);
      const photoUrls = [];
      for (let i = 0; i < Math.min(files.eventPhotos.length, 6); i++) {
        const f = files.eventPhotos[i];
        const path = `events/${eid}/photos/${i}-${safeName(f.name)}`;
        const evtContentType = (f.type && f.type !== 'application/octet-stream') ? f.type : (() => { const ext = f.name?.split('.').pop()?.toLowerCase(); return {jpg:'image/jpeg',jpeg:'image/jpeg',png:'image/png',gif:'image/gif',webp:'image/webp',heic:'image/heic',heif:'image/heif'}[ext] || 'application/octet-stream'; })();
        const { error: upErr } = await supabase.storage.from(bucket).upload(path, f, { upsert: true, contentType: evtContentType });
        if (upErr) {
          console.error('Event photo upload error:', upErr);
          // Try alternate bucket name
          const { error: upErr2 } = await supabase.storage.from('event-photos').upload(path, f, { upsert: true, contentType: evtContentType });
          if (upErr2) { console.error('Alternate bucket also failed:', upErr2); continue; }
          photoUrls.push(supabase.storage.from('event-photos').getPublicUrl(path).data.publicUrl);
        } else {
          photoUrls.push(supabase.storage.from(bucket).getPublicUrl(path).data.publicUrl);
        }
      }
      if (photoUrls.length > 0) {
        // Save first photo to photo_url (column that definitely exists)
        const { error: puErr } = await supabase.from('events').update({ photo_url: photoUrls[0] }).eq('id', eid);
        if (puErr) console.error('photo_url update failed:', puErr.message);
        // Try saving all photos to event_photos array column
        const { error: epErr } = await supabase.from('events').update({ event_photos: photoUrls }).eq('id', eid);
        if (epErr) console.error('event_photos update failed:', epErr.message);
        // Update local state
        newEvent.photo_url = photoUrls[0];
        newEvent.event_photos = photoUrls;
      }
    }
    // Send host confirmation email
    try {
      await fetch('/api/send-host-confirmation', {
        method: 'POST', headers: await authHeaders(),
        body: JSON.stringify({ hostEmail: form.email, hostName: form.contactName, eventName: form.eventName || (form.eventTypes||[])[0] || form.eventType, eventDate: form.date, eventType: (form.eventTypes||[])[0] || form.eventType }),
      });
    } catch (e) { console.error('Host confirmation email failed:', e); }
    // Notify admin of new event submission (styled email with gold button)
    authHeaders().then(h => fetch('/api/send-event-admin-notification', { method:'POST', headers: h,
      body: JSON.stringify({ eventName: form.eventName||(form.eventTypes||[])[0]||form.eventType, hostName: form.contactName, hostEmail: form.email, eventDate: form.date, eventType: (form.eventTypes||[])[0]||form.eventType, eventZip: form.eventZip }),
    })).catch(e=>console.error('API call failed:',e));
    // If admin-posted (auto-approved) and NOT a guest-only event, notify vendors
    if (adminMode && form.vendorDiscovery !== 'none') {
      authHeaders().then(h => fetch('/api/notify-matching-vendors',{method:'POST',headers: h,body:JSON.stringify({eventName:form.eventName||form.eventType,eventType:Array.isArray(form.eventTypes)?form.eventTypes[0]:(form.eventType||'Other'),eventTypes:Array.isArray(form.eventTypes)?form.eventTypes:[form.eventType].filter(Boolean),eventZip:form.eventZip,eventDate:form.date,categoriesNeeded:form.vendorCategories||[],servicesNeeded:form.servicesNeeded||[],orgName:form.orgName||'',eventLink:form.eventLink||'',vendorLink:form.vendorLink||''})})).catch(e=>console.error('Instant notify failed:',e));
    }

    // Generate individual event rows for recurring events
    const allNewEvents = newEvent ? [newEvent] : [];
    if (newEvent && form.isRecurring) {
      try {
        const dates = [];

        if (form.recurrenceType === 'specific' && Array.isArray(form.specificDates)) {
          // Specific dates mode — use the manually selected dates
          for (const dateStr of form.specificDates) {
            if (dateStr && dateStr !== form.date) dates.push(dateStr);
          }
        } else {
          // Schedule mode — generate dates from recurrence pattern
          const baseDate = new Date(form.date + 'T12:00:00');
          const maxOccurrences = form.recurrenceEndType === 'after' ? (form.recurrenceCount || 4) : 26;
          const endDate = form.recurrenceEndType === 'ondate' && form.recurrenceEndDate ? new Date(form.recurrenceEndDate + 'T12:00:00') : new Date(baseDate.getTime() + 365 * 24 * 60 * 60 * 1000);

          if (form.recurrenceFrequency === 'daily') {
            for (let i = 1; i < maxOccurrences; i++) {
              const d = new Date(baseDate.getTime() + i * 24 * 60 * 60 * 1000);
              if (d > endDate) break;
              dates.push(d.toISOString().split('T')[0]);
            }
          } else if (form.recurrenceFrequency === 'weekly') {
            for (let i = 1; i < maxOccurrences; i++) {
              const d = new Date(baseDate.getTime() + i * 7 * 24 * 60 * 60 * 1000);
              if (d > endDate) break;
              dates.push(d.toISOString().split('T')[0]);
            }
          } else if (form.recurrenceFrequency === 'biweekly') {
            for (let i = 1; i < maxOccurrences; i++) {
              const d = new Date(baseDate.getTime() + i * 14 * 24 * 60 * 60 * 1000);
              if (d > endDate) break;
              dates.push(d.toISOString().split('T')[0]);
            }
          } else if (form.recurrenceFrequency === 'monthly') {
            for (let i = 1; i < maxOccurrences; i++) {
              const d = new Date(baseDate);
              d.setMonth(d.getMonth() + i);
              if (d > endDate) break;
              dates.push(d.toISOString().split('T')[0]);
            }
          } else if (form.recurrenceFrequency === 'custom' && form.recurrenceWeekInterval) {
            for (let i = 1; i < maxOccurrences; i++) {
              const d = new Date(baseDate.getTime() + i * form.recurrenceWeekInterval * 7 * 24 * 60 * 60 * 1000);
              if (d > endDate) break;
              dates.push(d.toISOString().split('T')[0]);
            }
          }
        }

        // Insert each occurrence as a separate event row
        for (const dateStr of dates) {
          const recurPayload = { ...eventPayload, date: dateStr, source: 'Recurring Series' };
          // Remove columns that might not exist
          const { vendor_discovery: _vd, event_link: _el, vendor_notes: _vn, event_goer_notes: _egn, share_with_event_goers: _sweg, allow_duplicate_categories: _adc, allow_duplicate_subcategories: _ads, address: _addr, expected_attendance: _ea, indoor_outdoor: _io, electric_available: _elec, table_provided: _tp, table_size: _ts, vendor_subcategories: _vsub, event_types: _et, ...safePayload } = recurPayload;
          const { data: recurEvent } = await supabase.from('events').insert(safePayload).select().single();
          if (recurEvent) allNewEvents.push(recurEvent);
        }
      } catch (recurErr) { console.error('Recurring event generation error:', recurErr); }
    }

    // Add all events to state (original + recurring instances)
    if (allNewEvents.length > 0) {
      const mapped = allNewEvents.map(dbEventToApp);
      setUserEvents(prev => { const ids = new Set(prev.map(e=>e.id)); return [...mapped.filter(e=>!ids.has(e.id)), ...prev]; });
      setAllEvents(prev => { const ids = new Set(prev.map(e=>e.id)); return [...mapped.filter(e=>!ids.has(e.id)), ...prev]; });
    }
    setHostEvent(form);
    setHostConfirm({ ref: generateRef(), email: form.email, eventName: form.eventName || (form.eventTypes||[])[0] || form.eventType, isPending: true });
    setHostSuccess(true);
    window.scrollTo({top:0, behavior:'smooth'});
  };

  return (
    <>
      <style>{styles}</style>
      <div className="app">
        {loadError && (
          <div style={{background:'#fdecea',borderBottom:'2px solid #f5c6c6',padding:'10px 20px',
            fontSize:13,color:'#8b1a1a',fontWeight:600,textAlign:'center',zIndex:200,position:'relative'}}>
            ⚠ {loadError}
          </div>
        )}
        <nav className="nav">
          <div className="nav-logo" style={{cursor:'pointer',background:'none'}} onClick={()=>{setTab('home');setMobileMenuOpen(false);window.scrollTo({top:0});}}><img src="/logo.svg" alt="South Jersey Vendor Market" style={{height:44,width:'auto',display:'block',background:'none',border:'none'}} /></div>
          {/* Admin + Auth + Hamburger on right */}
          <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',justifyContent:'flex-end'}}>
          {isAdmin && (
            <button onClick={()=>{setTab('admin');window.scrollTo({top:0});}} style={{background:'#c8a850',color:'#1a1410',border:'none',borderRadius:6,padding:'6px 14px',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:"'DM Sans',sans-serif",whiteSpace:'nowrap'}}>
              Admin{(pendingVendors.length + allEvents.filter(e=>e.status==='pending_review').length) > 0 ? ` (${pendingVendors.length + allEvents.filter(e=>e.status==='pending_review').length})` : ''}
            </button>
          )}
          {authUser && vendorProfile && tab !== 'vendor-dashboard' && (
            <button className="nav-dash-btn" onClick={()=>{setTab('vendor-dashboard');window.scrollTo({top:0});}} style={{background:'#c8a850',color:'#1a1410',border:'none',borderRadius:6,padding:'6px 12px',fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:"'DM Sans',sans-serif"}}>My Vendor Dashboard</button>
          )}
          {authUser && hasBeenHost && tab !== 'host-dashboard' && (
            <button className="nav-dash-btn" onClick={()=>{setTab('host-dashboard');window.scrollTo({top:0});}} style={{background:'#c8a850',color:'#1a1410',border:'none',borderRadius:6,padding:'6px 12px',fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:"'DM Sans',sans-serif"}}>My Host Dashboard</button>
          )}
          {eventGoerProfile && (authUser || eventGoerManageToken) && tab !== 'event-goer-dashboard' && (
            <button className="nav-dash-btn" onClick={()=>{setTab('event-goer-dashboard');window.scrollTo({top:0});}} style={{background:'#c8a850',color:'#1a1410',border:'none',borderRadius:6,padding:'6px 12px',fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:"'DM Sans',sans-serif"}}>Event Guest</button>
          )}
          {authUser ? (
            <button onClick={handleLogout} style={{background:'none',border:'1px solid #c8a850',color:'#c8a850',borderRadius:6,padding:'6px 16px',fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:"'DM Sans',sans-serif"}}>Log Out</button>
          ) : (
            <button onClick={()=>setShowAuthModal(true)} style={{background:'#c8a850',color:'#1a1410',border:'none',borderRadius:6,padding:'6px 16px',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:"'DM Sans',sans-serif"}}>Log In / Sign Up</button>
          )}
          <button className="hamburger-btn" onClick={()=>setMobileMenuOpen(!mobileMenuOpen)} aria-label="Menu">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#c8a850" strokeWidth="2" strokeLinecap="round">
              {mobileMenuOpen ? <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></> : <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>}
            </svg>
          </button>
          </div>
          {/* Menu panel */}
          {mobileMenuOpen && (
            <div className="mobile-menu">
              <div className="mobile-menu-header">
                <div style={{cursor:'pointer'}} onClick={()=>{navTo('home');}}><img src="/logo.svg" alt="South Jersey Vendor Market" style={{height:34,width:'auto',display:'block',background:'none',border:'none'}} /></div>
                <button onClick={()=>setMobileMenuOpen(false)} style={{background:'none',border:'none',cursor:'pointer',padding:4}}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#c8a850" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              {/* Auth — first item */}
              <div className="mobile-menu-section">
                {authUser ? (
                  <>
                    {vendorProfile && <button className={`mobile-menu-item${tab==='vendor-dashboard'?' active':''}`} onClick={()=>navTo('vendor-dashboard')}>Vendor Dashboard</button>}
                    {hasBeenHost && <button className={`mobile-menu-item${tab==='host-dashboard'?' active':''}`} onClick={()=>navTo('host-dashboard')}>Host Dashboard</button>}
                    {(eventGoerProfile && (authUser || eventGoerManageToken)) && <button className={`mobile-menu-item${tab==='event-goer-dashboard'?' active':''}`} onClick={()=>navTo('event-goer-dashboard')}>Event Guest Dashboard</button>}
                    {isAdmin && <button className={`mobile-menu-item${tab==='admin'?' active':''}`} onClick={()=>navTo('admin')} style={{color:'#e8c97a'}}>Admin Panel</button>}
                    <button className="mobile-menu-item" style={{color:'#c8a850'}} onClick={()=>{handleLogout();setMobileMenuOpen(false);}}>Log Out</button>
                  </>
                ) : (
                  <button className="mobile-menu-item" style={{color:'#c8a850',fontWeight:700}} onClick={()=>{setShowAuthModal(true);setMobileMenuOpen(false);}}>Log In / Sign Up</button>
                )}
              </div>
              {authUser && (
                <div className="mobile-menu-section">
                  <div className="mobile-menu-label">My Stuff</div>
                  <button className={`mobile-menu-item${tab==='messages'?' active':''}`} onClick={()=>navTo('messages')}>Messages{unreadCount>0?` (${unreadCount})`:''}</button>
                  <button className={`mobile-menu-item${tab==='my-calendar'||tab==='calendar'||tab==='host-calendar'?' active':''}`} onClick={()=>navTo('my-calendar')}>My Calendar</button>
                </div>
              )}
              <div className="mobile-menu-section">
                <div className="mobile-menu-label">Quick Actions</div>
                {(authUser && userEvents.length > 0) && <button className={`mobile-menu-item${tab==='matches'?' active':''}`} onClick={()=>navTo('matches')}>Browse Vendors</button>}
                <button className={`mobile-menu-item${tab==='host'?' active':''}`} onClick={()=>navTo('host')}>Add an Event</button>
                <button className={`mobile-menu-item${tab==='vendor'?' active':''}`} onClick={()=>navTo('vendor')}>Add a Vendor Profile</button>
                <button className={`mobile-menu-item${tab==='opportunities'?' active':''}`} onClick={()=>navTo('opportunities')}>Browse Opportunities</button>
              </div>
              <div className="mobile-menu-section">
                <div className="mobile-menu-label">Explore</div>
                <button className={`mobile-menu-item${tab==='home'?' active':''}`} onClick={()=>navTo('home')}>Home</button>
                <button className={`mobile-menu-item${tab==='upcoming-markets'?' active':''}`} onClick={()=>navTo('upcoming-markets')}>Upcoming Events</button>
              </div>
              <div className="mobile-menu-section">
                <button className={`mobile-menu-item${tab==='pricing'?' active':''}`} onClick={()=>navTo('pricing')}>Pricing</button>
                <button className="mobile-menu-item" onClick={()=>{setShowContactModal(true);setMobileMenuOpen(false);}}>Contact Us</button>
                <button className={`mobile-menu-item${tab==='tos'?' active':''}`} onClick={()=>navTo('tos')}>Terms</button>
              </div>
            </div>
          )}
          <div className="nav-tabs">
            <button className={`nav-tab${tab==="home"?" active":""}`} onClick={()=>{setTab("home");window.scrollTo({top:0});}}>Home</button>
            <button className={`nav-tab${tab==="upcoming-markets"?" active":""}`} onClick={()=>{setTab("upcoming-markets");window.scrollTo({top:0});}}>Upcoming Events</button>
            <div className="nav-group">
              <div className="nav-group-label">&#128717; Vendors</div>
              <div className="nav-group-items">
                <button className={`nav-tab${tab==="vendor"?" active":""}`} onClick={()=>{setTab("vendor");window.scrollTo({top:0});}}>Add a Vendor Profile</button>
                <button className={`nav-tab${tab==="opportunities"?" active":""}`} onClick={()=>{setTab("opportunities");window.scrollTo({top:0});}}>Opportunities</button>
                <button className={`nav-tab${tab==="messages"?" active":""}`} onClick={()=>{setTab("messages");window.scrollTo({top:0});}}>
                  Messages{unreadCount>0?` (${unreadCount})`:''}
                </button>
              </div>
            </div>
            <div className="nav-group">
              <div className="nav-group-label">&#127918; Hosts</div>
              <div className="nav-group-items">
                <button className={`nav-tab${tab==="host"?" active":""}`} onClick={()=>{setTab("host");window.scrollTo({top:0});}}>Add an Event</button>
                {(!vendorProfile || userEvents.length > 0) && <button className={`nav-tab${tab==="matches"?" active":""}`} onClick={()=>{setTab("matches");window.scrollTo({top:0});}}>Browse Vendors</button>}
                <button className={`nav-tab${tab==="messages"?" active":""}`} onClick={()=>{setTab("messages");window.scrollTo({top:0});}}>
                  Messages{unreadCount>0?` (${unreadCount})`:''}
                </button>
              </div>
            </div>
            <button className={`nav-tab${tab==="pricing"?" active":""}`} onClick={()=>{setTab("pricing");window.scrollTo({top:0});}}>Pricing</button>
            <button className="nav-tab" onClick={()=>setShowContactModal(true)}>Contact Us</button>
            <button className={`nav-tab${tab==="tos"?" active":""}`} onClick={()=>{setTab("tos");window.scrollTo({top:0});}}>Terms</button>
            {isAdmin && <button className={`nav-tab${tab==="admin"?" active":""}`} onClick={()=>{setTab("admin");window.scrollTo({top:0});}}>Admin</button>}
            {authUser ? (
              <div className="nav-group">
                <div className="nav-group-label">&#128100; Account{vendorProfile && hasBeenHost ? ' (Vendor + Host)' : vendorProfile ? ' (Vendor)' : hasBeenHost ? ' (Host)' : ''}</div>
                <div className="nav-group-items">
                  {vendorProfile && <button className={`nav-tab${tab==="vendor-dashboard"?" active":""}`} onClick={()=>{setTab("vendor-dashboard");window.scrollTo({top:0});}}>Vendor Dashboard</button>}
                  {hasBeenHost && <button className={`nav-tab${tab==="host-dashboard"?" active":""}`} onClick={()=>{setTab("host-dashboard");window.scrollTo({top:0});}}>Host Dashboard</button>}
                  {vendorProfile && !hasBeenHost && <button className="nav-tab" style={{fontSize:12,color:'#a89a8a'}} onClick={()=>{setTab("host");window.scrollTo({top:0});}}>+ Add Host Role</button>}
                  {!vendorProfile && hasBeenHost && <button className="nav-tab" style={{fontSize:12,color:'#a89a8a'}} onClick={()=>{setTab("vendor");window.scrollTo({top:0});}}>+ Add Vendor Role</button>}
                  <button className={`nav-tab${tab==="my-calendar"?" active":""}`} onClick={()=>{setTab("my-calendar");window.scrollTo({top:0});}}>My Calendar</button>
                  <button className="nav-tab" onClick={handleLogout} style={{color:'#c8a84b'}}>Log Out</button>
                </div>
              </div>
            ) : (
              <button className="nav-tab" onClick={()=>setShowAuthModal(true)} style={{color:'#e8c97a',fontWeight:700}}>Log In</button>
            )}
          </div>
        </nav>

        {tab==='home' && (
          <div style={{background:'#0e0c0a'}}>
            {/* Text logo */}
            <div style={{textAlign:'center',paddingTop:32,paddingBottom:0}}>
              <h1 style={{margin:0,lineHeight:1}}>
                <span style={{fontFamily:"'Ibarra Real Nova',serif",fontSize:'clamp(56px,8vw,96px)',color:'#c8a850',display:'block',lineHeight:1.1,fontWeight:700,fontStyle:'italic'}}>South Jersey</span>
                <span style={{fontFamily:"'Lexend Deca',sans-serif",fontSize:'clamp(20px,3.2vw,38px)',color:'#fff',letterSpacing:6,fontWeight:700,textTransform:'uppercase',display:'block',marginTop:8}}>Vendor Market</span>
              </h1>
              <div style={{width:60,height:2,background:'#c8a850',margin:'10px auto 8px',borderRadius:1}} />
              <p style={{fontFamily:"'Public Sans',sans-serif",fontSize:'clamp(12px,1.2vw,15px)',color:'#c8a850',margin:'0 auto',lineHeight:1.4}}>Connecting vendors, events, and communities across South Jersey</p>
            </div>

            {/* How It Works — toggle */}
            <div style={{textAlign:'center',marginTop:20,marginBottom:8}}>
              <button onClick={()=>setShowHowItWorks(s=>!s)} style={{background:'none',border:'1px solid rgba(200,168,80,0.4)',color:'#c8a850',borderRadius:8,padding:'10px 24px',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:"'Public Sans',sans-serif",letterSpacing:0.3}}>
                {showHowItWorks ? 'Hide How It Works ▲' : 'How It Works ▼'}
              </button>
            </div>
            {showHowItWorks && (
              <div style={{padding:'0 24px',maxWidth:1200,margin:'0 auto',width:'100%',boxSizing:'border-box'}}>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))',gap:16}}>
                  {[
                    {title:'For Vendors',steps:['Create your profile — pick your category, set your travel radius, and upload photos. Offer more than one product or service? You can create separate listings for each under one account.','Get matched & discovered — hosts can find you and request you for their event, or you can browse open events and apply directly to the ones that fit.','Respond & manage — accept or decline host requests, message hosts directly, and manage all your events from your dashboard.']},
                    {title:'For Event Hosts',steps:['Add your event — enter your event details, pick the vendor categories you need, and set your preferences.','Find vendors — browse matched vendors in your area, or let vendors come to you through applications.','Book & manage — review applications, send booking requests, and coordinate everything in one place.']},
                    {title:'For Event Guests',steps:['Sign up for alerts — enter your zip code and pick the event types you love.','Discover events — browse upcoming markets, pop-ups, and festivals near you.','See who\'s there — check which vendors are attending before you go.']},
                  ].map(col=>(
                    <div key={col.title} style={{background:'rgba(200,168,80,0.08)',border:'1px solid rgba(200,168,80,0.2)',borderRadius:10,padding:'20px 18px'}}>
                      <div style={{fontFamily:"'Lexend Deca',sans-serif",fontSize:15,color:'#c8a850',fontWeight:700,marginBottom:12}}>{col.title}</div>
                      {col.steps.map((step,i)=>(
                        <div key={i} style={{display:'flex',gap:10,marginBottom:10}}>
                          <div style={{width:22,height:22,borderRadius:'50%',background:'#c8a850',color:'#0e0c0a',fontSize:12,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,marginTop:2}}>{i+1}</div>
                          <div style={{fontSize:13,color:'#c8b898',lineHeight:1.5}}><strong style={{color:'#fff'}}>{step.split(' — ')[0]}</strong> — {step.split(' — ')[1]}</div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
                <p style={{textAlign:'center',fontSize:12,color:'#a89a8a',lineHeight:1.6,maxWidth:600,margin:'16px auto 0'}}>
                  <strong style={{color:'#c8a850'}}>No event fees through our platform.</strong> South Jersey Vendor Market is a matching service — all event fees, service rates, and contracts are handled directly between vendors and hosts. We connect you; you handle the rest.
                </p>
              </div>
            )}

            {/* Three cards */}
            <div className="home-columns" style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:16,maxWidth:1200,width:'100%',margin:'0 auto',padding:'32px 32px 0'}}>
              {[
                { title:'Vendors', desc:'Create your profile, set your travel radius, and get matched with events looking for what you offer.',
                  buttons:[{label:'Add a Vendor Profile',tab:'vendor',signupRole:'vendor'},{label:'Browse Opportunities',tab:'opportunities'},...(authUser?[{label:'My Calendar',tab:'calendar'}]:[])] },
                { title:'Event Hosts', desc:'Post your event for free, browse vendor profiles, send booking requests, and manage it all in one place.',
                  buttons:[{label:'Add an Event',tab:'host',signupRole:'host'},{label:'Browse Vendors',tab:'matches'},...(authUser?[{label:'My Calendar',tab:'host-calendar'}]:[])] },
                { title:'Event Guests', desc:'Discover local markets, craft fairs, food festivals, and pop-up events happening across South Jersey.',
                  buttons:[{label:'Get Event Alerts',action:'eventGoerSignup',signupRole:'eventGoer'},{label:'Browse Upcoming Events',tab:'upcoming-markets'}] },
              ].map(card=>(
                <div key={card.title} className="home-col" style={{background:'#0e0c0a',borderRadius:10,padding:24,display:'flex',flexDirection:'column',textAlign:'center',border:'2px solid #c8a850'}}>
                  <h2 style={{fontFamily:"'Lexend Deca',sans-serif",fontSize:'clamp(16px,1.6vw,24px)',color:'#fff',margin:'0 0 8px',lineHeight:1.2,fontWeight:700}}>
                    {card.title}
                  </h2>
                  <p style={{fontFamily:"'Public Sans',sans-serif",fontSize:'clamp(11px,0.85vw,14px)',color:'#c8a850',lineHeight:1.5,margin:'0 0 16px'}}>
                    {card.desc}
                  </p>
                  <div style={{display:'flex',flexDirection:'column',gap:8,marginTop:'auto'}}>
                    {card.buttons.map(b=>(
                      <button key={b.label} onClick={()=>{
                        // If not logged in and this is a primary signup action, open unified signup
                        if (!authUser && b.signupRole) { openSignup(b.signupRole); return; }
                        if (b.action==='eventGoerSignup') { if(authUser) setShowEventGoerSignup(true); else openSignup('eventGoer'); return; }
                        setTab(b.tab); window.scrollTo({top:0});
                      }}
                        style={{width:'100%',background:'#0e0c0a',color:'#e8c97a',border:'1px solid rgba(255,255,255,0.4)',borderRadius:8,padding:'10px 0',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:"'Public Sans',sans-serif",letterSpacing:0.3}}>
                        {b.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Stats bar */}
            <div style={{padding:'16px 24px',display:'flex',justifyContent:'center',gap:'clamp(40px,8vw,100px)',flexWrap:'wrap',borderTop:'1px solid rgba(200,168,80,0.25)',borderBottom:'2px solid #c8a850',marginTop:32}}>
              {[
                {num: (siteCounts?.vendors ?? vendors.length) || '—', label:'Active Vendors'},
                {num: (siteCounts?.eventGuests ?? eventGoers.length) || '—', label:'Event Guests'},
                {num: (siteCounts?.events ?? opps.length) || '—', label:'Live Events'},
              ].map(s=>(
                <div key={s.label} style={{textAlign:'center'}}>
                  <div style={{fontFamily:"'Lexend Deca',sans-serif",fontSize:'clamp(22px,2.4vw,34px)',fontWeight:700,color:'#fff',lineHeight:1}}>{s.num}</div>
                  <div style={{fontFamily:"'Public Sans',sans-serif",fontSize:10,color:'#c8a850',letterSpacing:1.5,textTransform:'uppercase',fontWeight:600,marginTop:2}}>{s.label}</div>
                </div>
              ))}
            </div>

          </div>
        )}

        {tab==='vendor' && (
          <div className="section">
            {vendorSuccess ? (
              <>
                <div className="success-banner">
                  <div className="success-icon">🎉</div>
                  <h2>You're in the network!</h2>
                  <p>Your vendor profile has been submitted and is under review. We'll activate your listing within <span className="success-highlight">24 hours</span>.</p>
                  {vendorConfirm && (
                    <div style={{ marginTop:20, background:'rgba(255,255,255,0.08)', borderRadius:8, padding:'16px 20px', display:'inline-block', minWidth:280 }}>
                      <div style={{ fontSize:12, color:'#a89a8a', letterSpacing:2, textTransform:'uppercase', marginBottom:6 }}>Confirmation Number</div>
                      <div style={{ fontSize:22, fontWeight:700, color:'#e8c97a', letterSpacing:3, marginBottom:12 }}>{vendorConfirm.ref}</div>
                      <div style={{ fontSize:13, color:'#a89a8a', marginBottom:12 }}>Save this for your records.</div>
                      <a href={`mailto:${vendorConfirm.email}?subject=Your SJVM Add a Vendor Profile — ${vendorConfirm.ref}&body=Hi ${vendorConfirm.name},%0A%0AThank you for registering with South Jersey Vendor Market!%0A%0AYour confirmation number is: ${vendorConfirm.ref}%0A%0AWhat happens next:%0A• Your listing will be reviewed within 24 hours%0A• You'll be matched with nearby events automatically%0A• Check Messages for booking requests from hosts%0A%0A— South Jersey Vendor Market%0Asupport@southjerseyvendormarket.com`}
                        style={{ display:'inline-block', background:'#e8c97a', color:'#1a1410', padding:'9px 20px', borderRadius:6, fontSize:13, fontWeight:700, textDecoration:'none', fontFamily:'DM Sans,sans-serif' }}>
                        📧 Email yourself a copy
                      </a>
                    </div>
                  )}
                </div>
                <div style={{ background:'#fff', border:'1px solid #e8ddd0', borderRadius:10, padding:'24px 28px', marginTop:24 }}>
                  <div style={{ fontFamily:'Playfair Display,serif', fontSize:18, marginBottom:16 }}>What happens next</div>
                  {[
                    { icon:'🔍', title:'Profile Review', desc:"Our team reviews your submission within 24 hours and activates your listing." },
                    { icon:'📅', title:'Event Matching', desc:"When a host posts an event in your area and categories, you'll appear in their vendor list." },
                    { icon:'💬', title:'Booking Requests', desc:"Hosts send booking requests directly through the platform. Check your Messages tab." },
                  ].map(s => (
                    <div key={s.title} style={{ display:'flex', gap:14, marginBottom:16 }}>
                      <div style={{ fontSize:24, flexShrink:0 }}>{s.icon}</div>
                      <div><div style={{ fontWeight:600, fontSize:14, marginBottom:2 }}>{s.title}</div><div style={{ fontSize:13, color:'#7a6a5a' }}>{s.desc}</div></div>
                    </div>
                  ))}
                </div>
                <div style={{background:'#e8f4fd',border:'1px solid #b8d8f0',borderRadius:10,padding:'16px 20px',marginTop:24}}>
                  <div style={{fontWeight:700,fontSize:15,color:'#1a4a6b',marginBottom:6}}>Have another business or service?</div>
                  <p style={{fontSize:13,color:'#1a4a6b',lineHeight:1.6,margin:'0 0 12px'}}>You can create multiple listings under one account — one for each thing you offer. Each listing gets its own categories, photos, pricing, and event matches.</p>
                  <button style={{background:'#1a4a6b',color:'#fff',border:'none',borderRadius:6,padding:'10px 20px',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}} onClick={()=>{setVendorSuccess(false);setVendorConfirm(null);}}>+ Add Another Listing</button>
                </div>
              </>
            ) : (
              <>
                <div className="section-title">Add a Vendor Profile</div>
                <p className="section-sub">Join South Jersey's growing vendor community and get matched with events near you.</p>
                {authUser && vendorProfile && (
      <div style={{background:'#e8f4fd',border:'1px solid #b8d8f0',borderRadius:10,padding:'16px 20px',marginBottom:20,fontSize:14,color:'#1a4a6b',lineHeight:1.6}}>
        <strong>Adding another listing?</strong> This will create a separate profile under your account. Each listing gets its own categories, photos, and pricing — perfect if you offer different products or services.
      </div>
    )}
    <VendorForm onSubmit={handleVendorSubmit} setTab={setTab} authUser={authUser} setShowAuthModal={setShowAuthModal} openSignup={openSignup} defaultReferralCode={referralCode} />
              </>
            )}
          </div>
        )}

        {tab==='host' && (
          <div className="section" style={{maxWidth: hostSuccess ? 1060 : undefined}}>
            {hostSuccess ? (
              <HostSuccessMatches
                hostEvent={hostEvent}
                hostConfirm={hostConfirm}
                vendors={vendors}
                openMessage={openMessage}
                sendBookingRequest={sendBookingRequest}
                bookingRequests={bookingRequests}
                setTab={setTab}
                vendorCalendars={vendorCalendars}
                setVendorCalendars={setVendorCalendars}
                onSubmitAnother={() => { setHostSuccess(false); setHostEvent(null); setHostConfirm(null); setHostFormType(null); }}
              />
            ) : !hostFormType ? (
              <>
                <div style={{background:'linear-gradient(135deg,#1a1410,#2d2118)',padding:'48px 40px',textAlign:'center',position:'relative',overflow:'hidden',borderRadius:12,marginBottom:32}}>
                  <div style={{position:'absolute',inset:0,background:'radial-gradient(ellipse 60% 80% at 50% 50%,rgba(232,201,122,.08),transparent)'}} />
                  <h2 style={{fontFamily:'Playfair Display,serif',fontSize:32,color:'#fff',marginBottom:8,position:'relative'}}>Add an <em style={{color:'#e8c97a',fontStyle:'italic'}}>Event</em></h2>
                  <p style={{color:'#a89a8a',fontSize:15,maxWidth:500,margin:'0 auto',position:'relative'}}>What type of event would you like to post?</p>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))',gap:20,maxWidth:700,margin:'0 auto'}}>
                  <button onClick={()=>setHostFormType('opportunity')} style={{background:'#fff',border:'2px solid #c8a850',borderRadius:12,padding:'28px 24px',textAlign:'center',cursor:'pointer',transition:'all 0.15s',fontFamily:'DM Sans,sans-serif'}}>
                    <div style={{fontSize:32,marginBottom:10}}>🛍️</div>
                    <div style={{fontFamily:'Playfair Display,serif',fontSize:20,fontWeight:700,color:'#1a1410',marginBottom:8}}>Vendor Opportunity</div>
                    <p style={{fontSize:13,color:'#7a6a5a',lineHeight:1.6,margin:0}}>Post an event and find vendors to participate. You'll specify what vendor categories you need, booth details, and logistics. Vendors can apply or you can browse and invite them. Your event will also be visible to event guests on the Upcoming Events page.</p>
                  </button>
                  <button onClick={()=>setHostFormType('guest')} style={{background:'#fff',border:'2px solid #e8ddd0',borderRadius:12,padding:'28px 24px',textAlign:'center',cursor:'pointer',transition:'all 0.15s',fontFamily:'DM Sans,sans-serif'}}>
                    <div style={{fontSize:32,marginBottom:10}}>📅</div>
                    <div style={{fontFamily:'Playfair Display,serif',fontSize:20,fontWeight:700,color:'#1a1410',marginBottom:8}}>Guest Event</div>
                    <p style={{fontSize:13,color:'#7a6a5a',lineHeight:1.6,margin:0}}>Share a local event with the community. It will appear on the Upcoming Events page so people nearby can discover it. No vendor setup needed — just the event details.</p>
                  </button>
                </div>
              </>
            ) : hostFormType === 'guest' ? (
              <>
                <button onClick={()=>setHostFormType(null)} style={{background:'none',border:'none',color:'#c8a850',fontSize:13,cursor:'pointer',fontFamily:'DM Sans,sans-serif',marginBottom:8,padding:0}}>← Back to options</button>
                <HostGuestEventForm onSubmit={handleHostSubmit} authUser={authUser} setShowAuthModal={setShowAuthModal} openSignup={openSignup} />
              </>
            ) : (
              <>
                <button onClick={()=>setHostFormType(null)} style={{background:'none',border:'none',color:'#c8a850',fontSize:13,cursor:'pointer',fontFamily:'DM Sans,sans-serif',marginBottom:8,padding:0}}>← Back to options</button>
                <div className="section-title">Add a Vendor Opportunity</div>
                <p className="section-sub">Tell us about your event and we'll find the perfect vendors for you.</p>
                <HostForm onSubmit={handleHostSubmit} setTab={setTab} authUser={authUser} setShowAuthModal={setShowAuthModal} openSignup={openSignup} />
              </>
            )}
          </div>
        )}

        {tab==="matches"      && (loading
            ? <div style={{textAlign:'center',padding:'80px 20px',color:'#a89a8a',fontSize:16}}>Loading vendors…</div>
            : <MatchesPage vendors={vendors} openMessage={openMessage} sendBookingRequest={sendBookingRequest} bookingRequests={bookingRequests} setBookingRequests={setBookingRequests} hostEvent={hostEvent} setHostEvent={setHostEvent} userEvents={userEvents} setTab={setTab} vendorCalendars={vendorCalendars} setVendorCalendars={setVendorCalendars} authUser={authUser} setShowAuthModal={setShowAuthModal} setInquiryModal={setInquiryModal} setEventMessageModal={setEventMessageModal} />)}
        {tab==="upcoming-markets" && (loading
          ? <div style={{textAlign:'center',padding:'80px 20px',color:'#a89a8a',fontSize:16}}>Loading events…</div>
          : <UpcomingMarketsPage opps={opps} acceptedBookings={acceptedBookings} setTab={setTab} setShowAuthModal={setShowAuthModal} setShowEventGoerSignup={setShowEventGoerSignup} eventGoerProfile={eventGoerProfile} authUser={authUser} vendorProfile={vendorProfile} isAdmin={isAdmin} />)}
        {tab==="opportunities" && (loading
          ? <div style={{textAlign:'center',padding:'80px 20px',color:'#a89a8a',fontSize:16}}>Loading events…</div>
          : <OpportunitiesPage opps={opps} acceptedBookings={acceptedBookings} authUser={authUser} vendorProfile={vendorProfile} allVendorProfiles={allVendorProfiles} setVendorProfile={setVendorProfile} setShowAuthModal={setShowAuthModal} messageEventHost={messageEventHost} highlightOppId={highlightOppId} setHighlightOppId={setHighlightOppId} />)}
        {tab==="pricing"       && <PricingPage setTab={setTab} authUser={authUser} vendorProfile={vendorProfile} userEvents={userEvents} setShowAuthModal={setShowAuthModal} setShowContactModal={setShowContactModal} />}
        {tab==="admin"         && <AdminPage opps={opps} setOpps={setOpps} allEvents={allEvents} setAllEvents={setAllEvents} acceptedBookings={acceptedBookings} vendorSubs={vendorSubs} vendors={vendors} setVendors={setVendors} pendingVendors={pendingVendors} setPendingVendors={setPendingVendors} isAdmin={isAdmin} eventGoers={eventGoers} setEventGoers={setEventGoers} handleHostSubmit={handleHostSubmit} authUser={authUser} setTab={setTab} />}
        {tab==="messages"      && <MessagesPage conversations={conversations} setConversations={setConversations} activeConvoId={activeConvoId} setActiveConvoId={setActiveConvoId} bookingRequests={bookingRequests} setBookingRequests={setBookingRequests} authUser={authUser} vendorProfile={vendorProfile} userEvents={userEvents} loadMessages={loadMessages} setTab={setTab} />}
        {tab==="tos"           && <TosPage setTab={setTab} />}
        {(tab==="my-calendar" || tab==="calendar" || tab==="host-calendar") && <MyCalendarPage authUser={authUser} vendorProfile={vendorProfile} userEvents={userEvents} setTab={setTab} />}
        {tab==="vendor-dashboard" && authUser && vendorProfile && <VendorDashboard user={authUser} vendorProfile={vendorProfile} setVendorProfile={setVendorProfile} allVendorProfiles={allVendorProfiles} bookingRequests={bookingRequests} setTab={setTab} setShowContactModal={setShowContactModal} setShowFeedbackModal={setShowFeedbackModal} conversations={conversations} setConversations={setConversations} setActiveConvoId={setActiveConvoId} unreadCount={unreadCount} opps={opps} setHighlightOppId={setHighlightOppId} acceptedBookings={acceptedBookings} />}
        {tab==="host-dashboard"   && authUser && <HostDashboard user={authUser} userEvents={userEvents} setUserEvents={setUserEvents} acceptedBookings={acceptedBookings} setTab={setTab} setShowContactModal={setShowContactModal} setShowFeedbackModal={setShowFeedbackModal} unreadCount={unreadCount} conversations={conversations} openMessage={openMessage} setAllEvents={setAllEvents} setOpps={setOpps} setHostEventFromDashboard={(e)=>{const svcNeeded=(()=>{try{return typeof e.services_needed==='string'?JSON.parse(e.services_needed):(e.services_needed||[]);}catch{return[];}})();setHostEvent({eventName:e.event_name,eventType:e.event_type,eventZip:e.zip,date:e.date,startTime:e.start_time,endTime:e.end_time,contactName:e.contact_name,email:e.contact_email,vendorCategories:e.categories_needed||[],servicesNeeded:svcNeeded,vendorCount:e.spots,budget:e.booth_fee,notes:e.notes,eventId:e.id,source:e.source});}} />}
        {tab==="event-goer-dashboard" && eventGoerProfile && (authUser || eventGoerManageToken) && <EventGoerDashboard profile={eventGoerProfile} setProfile={setEventGoerProfile} authUser={authUser} manageToken={eventGoerManageToken} opps={opps} acceptedBookings={acceptedBookings} setShowContactModal={setShowContactModal} setShowFeedbackModal={setShowFeedbackModal} />}
      </div>
      {/* Site Footer */}
      <footer style={{background:'#1a1208',padding:'32px 24px',marginTop:20,textAlign:'center',borderTop:'1px solid rgba(200,168,80,0.15)'}}>
        <div style={{fontFamily:'Playfair Display,serif',fontSize:18,color:'#e8c97a',marginBottom:8}}>South Jersey Vendor Market</div>
        <p style={{fontSize:13,color:'#a89a8a',marginBottom:16}}>Connecting vendors and events across South Jersey</p>
        <div style={{display:'flex',gap:16,justifyContent:'center',flexWrap:'wrap',marginBottom:16}}>
          <button onClick={()=>setShowContactModal(true)} style={{background:'transparent',color:'#e8c97a',border:'1px solid #e8c97a',borderRadius:6,padding:'8px 20px',fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>Contact Us</button>
          <button onClick={()=>setShowFeedbackModal(true)} style={{background:'transparent',color:'#e8c97a',border:'1px solid #e8c97a',borderRadius:6,padding:'8px 20px',fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>Give Feedback</button>
          <button onClick={()=>{setTab('tos');window.scrollTo({top:0});}} style={{background:'transparent',color:'#a89a8a',border:'1px solid rgba(168,154,138,0.3)',borderRadius:6,padding:'8px 20px',fontSize:13,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>Terms of Service</button>
          <button onClick={()=>{setTab('pricing');window.scrollTo({top:0});}} style={{background:'transparent',color:'#a89a8a',border:'1px solid rgba(168,154,138,0.3)',borderRadius:6,padding:'8px 20px',fontSize:13,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>Pricing</button>
        </div>
        <div style={{fontSize:11,color:'#5a4a3a'}}>support@southjerseyvendormarket.com</div>
      </footer>
      {showAuthModal && <AuthModal onClose={()=>{setShowAuthModal(false);setAuthModalMode(null);setAuthModalRole(null);}} onAuth={()=>{}} defaultEmail={authEmail} setTab={setTab} setShowEventGoerSignup={setShowEventGoerSignup} defaultMode={authModalMode} defaultRole={authModalRole} />}
      {showContactModal && <ContactModal onClose={()=>setShowContactModal(false)} userName={authUser?.user_metadata?.full_name||''} userEmail={authUser?.email||''} />}
      {showFeedbackModal && <FeedbackModal onClose={()=>setShowFeedbackModal(false)} userEmail={authUser?.email||''} />}
      {showEventGoerSignup && <EventGoerSignupModal onClose={()=>setShowEventGoerSignup(false)} defaultEmail={authUser?.email||''} defaultName={vendorProfile?.contact_name||''} onSuccess={()=>{ supabase.from('event_goers').select('*').eq('active',true).then(({data})=>{if(data)setEventGoers(data);}); if(authUser) supabase.from('event_goers').select('*').eq('email',authUser.email).limit(1).single().then(({data})=>{if(data)setEventGoerProfile(data);}); }} onGoToDashboard={authUser ? ()=>{setTab('event-goer-dashboard');window.scrollTo({top:0});} : null} />}
      {inquiryModal && <InquiryModal vendor={inquiryModal.vendor} onClose={()=>setInquiryModal(null)} onSend={(type,msg)=>{openMessage(inquiryModal.vendor,type,msg);setInquiryModal(null);}} />}
      {eventMessageModal && <EventMessageModal vendor={eventMessageModal.vendor} eventName={eventMessageModal.eventName} onClose={()=>setEventMessageModal(null)} onSend={async(msg)=>{await openMessage(eventMessageModal.vendor,null,msg);setEventMessageModal(null);}} />}
    </>
  );
}

export default AppInner;
